import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebase/config'; 
import { 
    doc, 
    updateDoc, 
    serverTimestamp,
    getDoc
} from 'firebase/firestore';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { saveToFirestore, sortAlphabetically } from '../../firebase/cleanPayload';
import { cleanNumeric } from '../../utils/segurosUtils';

// Importación del Servicio unificado de Medios de Pago (Reutilización Senior)
import { 
    formatMedioPagoAbreviado, 
    registrarMedioPagoCliente,
    MARCAS_TARJETA_ADMITIDAS
} from '../../utils/paymentService';

// Listas estáticas
import { 
    endosoEstadosGestion, 
    endosoTiposGestion, 
    formaPago as formasPagoList 
} from '../../data/listasDesplegables';

/**
 * FormularioEndoso - Gestiona endosos, solicitudes de cambios y modificaciones de medios de pago.
 * Totalmente optimizado e integrado con el nuevo paymentService unificado.
 */
const FormularioEndoso = ({ 
    poliza, 
    clientData, 
    initialData, 
    onSuccess, 
    onCancel 
}) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [formData, setFormData] = useState({
        fechaCarga: new Date().toISOString().split('T')[0],
        tipoGestion: '',
        estadoGestion: 'Pendiente',
        tramite: '',
        observaciones: '',
        quienGestiono: '', 
        formaPago: '',
        medioPagoId: '' 
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fullClient, setFullClient] = useState(null); 
    const [isAddingNewMedio, setIsAddingNewMedio] = useState(false);
    const [nuevoMedio, setNuevoMedio] = useState({
        marca: '', banco: '', numero: '', vencimientoTarjeta: '', cbu: '' 
    });
    
    // Banner de control para errores de validación
    const [errorMsg, setErrorMsg] = useState(null);

    const estadosGestion = endosoEstadosGestion || ['Pendiente', 'Gestionado', 'Reclamado']; 
    const tiposGestion = endosoTiposGestion || ['Endoso', 'Cambio de Método de Pago'];

    // --- 3. CARGA ASÍNCRONA DE LA FICHA EXTENDIDA DEL CLIENTE SI FALTA ---
    useEffect(() => {
        const fetchEssentialClient = async () => {
            try {
                const cId = clientData?.id || poliza?.clienteId || initialData?.clienteId;
                if (cId) {
                    const cSnap = await getDoc(doc(db, 'public/data/clientes', cId));
                    if (cSnap.exists()) {
                        setFullClient({ id: cSnap.id, ...cSnap.data() });
                    }
                }
            } catch (error) { 
                console.error("Error al cargar datos esenciales del cliente en endosos:", error); 
            }
        };
        fetchEssentialClient();
    }, [clientData, poliza, initialData]);

    // --- 4. HIDRATACIÓN DE DATOS PREVIOS (EDICIÓN) ---
    useEffect(() => {
        if (initialData) {
            const savedId = String(initialData.numeroCbu || initialData.numeroTarjeta || initialData.numero || initialData.cbu || '');
            setFormData({ 
                ...initialData,
                medioPagoId: savedId 
            });
        }
    }, [initialData]);

    const esCambioDePago = formData.tipoGestion === 'Cambio de Método de Pago';

    // --- 5. MEMORIZACIÓN Y ORDENAMIENTO DE LISTAS (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedTiposGestion = useMemo(() => {
        return [...tiposGestion].sort((a, b) => a.localeCompare(b));
    }, [tiposGestion]);

    const sortedFormasPago = useMemo(() => {
        return [...formasPagoList].sort((a, b) => a.localeCompare(b));
    }, []);

    // ◄ MEJORA: Memorizar y ordenar alfabéticamente las marcas importadas del servicio
    const sortedMarcasAdmitidas = useMemo(() => {
        return sortAlphabetically(MARCAS_TARJETA_ADMITIDAS || []);
    }, []);

    // Construcción de medios de pago del Asegurado formateados visualmente con el servicio
    const listaMediosPago = useMemo(() => {
        const clienteActual = fullClient || clientData;
        if (!clienteActual || !formData.formaPago) return [];
        
        const forma = String(formData.formaPago).toUpperCase().trim();
        if (forma.includes('TARJETA')) {
            return (clienteActual.tarjetas || []).map(t => ({ 
                id: String(t.numeroTarjeta || t.numero || ''), 
                label: formatMedioPagoAbreviado(t, 'Tarjeta de Crédito'),
                original: t
            }));
        }
        if (forma === 'CBU') {
            return (clienteActual.cbus || []).map(c => ({ 
                id: String(c.numeroCbu || c.cbu || c.numero || ''), 
                label: formatMedioPagoAbreviado(c, 'CBU'),
                original: c
            }));
        }
        return [];
    }, [formData.formaPago, fullClient, clientData]);

    // --- 6. MANIPULACIÓN DE CAMPOS ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null); // Limpiamos cualquier banner de error previo al tipear
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (name === 'medioPagoId') {
            setIsAddingNewMedio(value === 'NEW');
        }
        if (name === 'formaPago') {
            setFormData(prev => ({ ...prev, [name]: value, medioPagoId: '' }));
            setIsAddingNewMedio(false);
            setNuevoMedio({ marca: '', banco: '', numero: '', vencimientoTarjeta: '', cbu: '' });
        }
    };

    const handleNuevoMedioChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null); // Limpiamos errores de entrada
        let cleanValue = value;
        
        if (name === 'cbu' || name === 'numero') {
            cleanValue = cleanNumeric(value);
        }
        if (name === 'marca' || name === 'banco') {
            cleanValue = value.toUpperCase();
        }

        setNuevoMedio(prev => ({ ...prev, [name]: cleanValue }));
    };

    // --- 7. PROCESAMIENTO Y GUARDADO FINAL ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!poliza || isSubmitting) return;

        if (esCambioDePago && (!formData.formaPago || !formData.medioPagoId)) {
            setErrorMsg("Debe completar todos los datos de forma y medio de pago.");
            return;
        }
        
        const currentFormaPago = String(formData.formaPago || '').toUpperCase().trim();
        setIsSubmitting(true);
        setErrorMsg(null);
        
        try {
            let notaSistema = "";
            let detallesPagoParaEndoso = {};

            // Lógica para Cambio de Medio de Pago
            if (esCambioDePago) {
                // Caso A: Cargar nuevo medio delegando de forma segura en nuestro servicio unificado
                if (isAddingNewMedio) {
                    // El servicio validará e impactará Firestore. Lanza error si algo falla.
                    await registrarMedioPagoCliente(clientData.id, nuevoMedio, formData.formaPago);

                    if (currentFormaPago === 'CBU') {
                        detallesPagoParaEndoso = { 
                            banco: nuevoMedio.banco || 'BANCO S/D', 
                            numeroCbu: nuevoMedio.cbu 
                        };
                        notaSistema = `[SISTEMA: Nuevo CBU ****${nuevoMedio.cbu.slice(-6)}]`;
                    } else {
                        detallesPagoParaEndoso = {
                            marca: nuevoMedio.marca || 'S/D',
                            banco: nuevoMedio.banco || 'BANCO S/D',
                            numeroTarjeta: nuevoMedio.numero,
                            vencimientoTarjeta: nuevoMedio.vencimientoTarjeta || ''
                        };
                        notaSistema = `[SISTEMA: Nueva Tarjeta ${nuevoMedio.marca} ****${nuevoMedio.numero.slice(-4)}]`;
                    }
                } 
                // Caso B: Usar un medio de pago existente
                else {
                    const medioExistente = listaMediosPago.find(m => String(m.id) === String(formData.medioPagoId));
                    if (medioExistente) {
                        const orig = medioExistente.original;
                        if (currentFormaPago === 'CBU') {
                            detallesPagoParaEndoso = { 
                                banco: orig.banco || 'BANCO S/D', 
                                numeroCbu: orig.numeroCbu || orig.cbu || orig.numero || '' 
                            };
                        } else {
                            detallesPagoParaEndoso = { 
                                banco: orig.banco || 'BANCO S/D', 
                                marca: orig.marca || 'S/D', 
                                numeroTarjeta: orig.numeroTarjeta || orig.numero || '', 
                                vencimientoTarjeta: orig.vencimientoTarjeta || orig.vencimiento || '' 
                            };
                        }
                    } else if (initialData) {
                        detallesPagoParaEndoso = { 
                            banco: initialData.banco, 
                            numeroTarjeta: initialData.numeroTarjeta, 
                            numeroCbu: initialData.numeroCbu,
                            marca: initialData.marca,
                            vencimientoTarjeta: initialData.vencimientoTarjeta 
                        };
                    }
                }

                // ACTUALIZACIÓN DE PÓLIZA RAÍZ (Solo si el endoso cambia a estado 'Gestionado')
                if (formData.estadoGestion === 'Gestionado') {
                    const polizaRef = doc(db, `public/data/clientes/${clientData.id}/polizas`, poliza.id);
                    // Formateamos visualmente usando el servicio
                    const abreviadoActual = formatMedioPagoAbreviado(detallesPagoParaEndoso, formData.formaPago);
                    
                    const updatesPoliza = {
                        medioPago: abreviadoActual,
                        formaPago: formData.formaPago,
                        ultimaModificacion: serverTimestamp()
                    };

                    if (!poliza.medioPagoEmision) {
                        if (poliza.medioPago && poliza.medioPago !== 'S/D') {
                            updatesPoliza.medioPagoEmision = poliza.medioPago;
                        } else {
                            const originalRaw = {
                                marca: poliza.marca || '',
                                banco: poliza.banco || '',
                                numeroTarjeta: poliza.numeroTarjeta || poliza.nroTarjeta || poliza.numero || '',
                                numeroCbu: poliza.numeroCbu || poliza.cbu || ''
                            };
                            updatesPoliza.medioPagoEmision = formatMedioPagoAbreviado(originalRaw, poliza.formaPago);
                        }
                    }
                    await updateDoc(polizaRef, updatesPoliza);
                }
            }

            // PREPARACIÓN DEL PAYLOAD FINAL PARA EL DOCUMENTO DE ENDOSO
            const payload = { 
                ...formData,
                banco: esCambioDePago ? (detallesPagoParaEndoso.banco || null) : null,
                numeroTarjeta: (esCambioDePago && currentFormaPago.includes('TARJETA')) ? (detallesPagoParaEndoso.numeroTarjeta || null) : null,
                numeroCbu: (esCambioDePago && currentFormaPago === 'CBU') ? (detallesPagoParaEndoso.numeroCbu || null) : null,
                marca: (esCambioDePago && currentFormaPago.includes('TARJETA')) ? (detallesPagoParaEndoso.marca || null) : null,
                vencimientoTarjeta: (esCambioDePago && currentFormaPago.includes('TARJETA')) ? (detallesPagoParaEndoso.vencimientoTarjeta || null) : null,
                polizaId: poliza.id,
                nPoliza: poliza.nPoliza || 'PENDIENTE',
                clienteId: clientData.id,
                clienteNombre: clientData.nombre || '',
                clienteApellido: clientData.apellido || '',
                clienteDni: clientData.dni || '',
                clienteCuit: clientData.cuit || '',
                riesgo: poliza.riesgo || '',
                riesgoId: poliza.riesgoId || '',
                subriesgo: poliza.subriesgo || '',
                compania: poliza.compania || '',
                companiaId: poliza.companiaId || '',
                canal: poliza.canal || '',
                canalNombre: poliza?.canalNombre || poliza?.subcanal || formData.canalNombre || initialData?.canalNombre || '',
                canalNombreId: poliza.canalNombreId || '',
                subcanalNombre: poliza.subcanalNombre || '',
                subcanalNombreId: poliza.subcanalNombreId || '',
                observaciones: notaSistema ? `${formData.observaciones} ${notaSistema}`.trim() : formData.observaciones,
            };

            if (!esCambioDePago) {
                delete payload.formaPago;
            }

            delete payload.medioPagoId; 
            delete payload.id;
            delete payload.path;

            const endososPath = `public/data/clientes/${clientData.id}/polizas/${poliza.id}/endosos`;
            // saveToFirestore estampará de forma totalmente transparente la auditoría del operador unificado (Hito 3)
            await saveToFirestore(endososPath, payload, initialData?.id);

            onSuccess(initialData ? 'Endoso actualizado correctamente.' : 'Nuevo endoso registrado correctamente.', 'success');
            
        } catch (error) {
            console.error("Error crítico al guardar endoso:", error);
            setErrorMsg(error.message || "Ocurrió un error inesperado al procesar el endoso.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white">
            <form id="endosoForm" onSubmit={handleSubmit} className="space-y-6">
                
                {/* BANNER DE ERROR PARA VALIDACIONES DE PAGO */}
                {errorMsg && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 animate-fade-in select-none">
                        <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>{errorMsg}</span>
                    </div>
                )}

                <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white text-left shadow-sm transition-all duration-300">
                    <legend className="px-3 text-lg font-bold text-gray-700 bg-white absolute -top-3.5 left-4 tracking-tight">Detalles del Endoso</legend>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        {/* Fecha de Carga */}
                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha de Carga *</label>
                            <input type="date" name="fechaCarga" value={formData.fechaCarga} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required />
                        </div>

                        {/* Tipo de Gestión */}
                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo de Gestión *</label>
                            <select name="tipoGestion" value={formData.tipoGestion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required>
                                <option value="">Seleccione...</option>
                                {sortedTiposGestion.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        {/* SECCIÓN CONDICIONAL: CAMBIO DE MÉTODO DE PAGO */}
                        {esCambioDePago && (
                            <>
                                <div className="animate-fade-in col-span-1">
                                    <label className="block text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1.5 pl-1">Nueva Forma de Pago *</label>
                                    <select 
                                        name="formaPago" 
                                        value={formData.formaPago} 
                                        onChange={handleChange} 
                                        className="w-full border border-blue-200 rounded-xl px-4 py-2.5 text-sm font-bold text-blue-700 outline-none bg-blue-50/10 h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                        required={esCambioDePago}
                                    >
                                        <option value="">Seleccione...</option>
                                        {sortedFormasPago.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>
                                
                                {formData.formaPago && (
                                    <div className="animate-fade-in col-span-1">
                                        <label className="block text-[11px] font-bold text-orange-600 uppercase tracking-wide mb-1.5 pl-1">Seleccionar Medio del Cliente *</label>
                                        <select 
                                            name="medioPagoId" 
                                            value={formData.medioPagoId || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-orange-200 rounded-xl px-4 py-2.5 text-sm font-bold text-orange-800 outline-none bg-white h-[46px] focus:border-orange-400 focus:ring-1 focus:ring-orange-100 transition-all"
                                            required={esCambioDePago}
                                        >
                                            <option value="">-- Seleccione medio registrado --</option>
                                            {listaMediosPago.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                                            <option value="NEW" className="text-blue-600 font-black">+ CARGAR NUEVO Y VINCULAR...</option>
                                        </select>
                                    </div>
                                )}

                                {/* ALTA RÁPIDA DE MEDIO DE PAGO */}
                                {isAddingNewMedio && (
                                    <div className="md:col-span-2 p-5 bg-slate-50 border border-dashed border-slate-300 rounded-xl grid grid-cols-2 gap-4 animate-fade-in text-left">
                                        <p className="col-span-2 text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 select-none">Ficha de Nuevo Medio de Pago</p>
                                        {formData.formaPago === 'CBU' ? (
                                            <>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1">Banco</label>
                                                    <input type="text" name="banco" placeholder="Nombre del Banco" value={nuevoMedio.banco} onChange={handleNuevoMedioChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required={isAddingNewMedio} />
                                                </div>
                                                <div className="col-span-1"></div>
                                                <div className="col-span-2">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1">Número de CBU (22 Dígitos)</label>
                                                    <input type="text" name="cbu" placeholder="0000000000000000000000" maxLength={22} value={nuevoMedio.cbu} onChange={handleNuevoMedioChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono tracking-widest text-left text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required={isAddingNewMedio} />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* Cambio estratégico: select dinámico e inmutable conectado al listado maestro */}
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1">Marca</label>
                                                    <select 
                                                        name="marca" 
                                                        value={nuevoMedio.marca} 
                                                        onChange={handleNuevoMedioChange} 
                                                        className="w-full border border-slate-200 rounded-xl px-3 text-sm text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all font-bold"
                                                        required={isAddingNewMedio}
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        {sortedMarcasAdmitidas.map(m => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1">Banco Emisor</label>
                                                    <input type="text" name="banco" value={nuevoMedio.banco} onChange={handleNuevoMedioChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required={isAddingNewMedio} placeholder="Ej: GALICIA" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1 text-left">Número de Tarjeta</label>
                                                    <input type="text" name="numero" maxLength={16} value={nuevoMedio.numero} onChange={handleNuevoMedioChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono tracking-widest text-left text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required={isAddingNewMedio} placeholder="16 dígitos sin espacios" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 pl-1">Vencimiento</label>
                                                    <input type="text" name="vencimientoTarjeta" placeholder="MM/AA" value={nuevoMedio.vencimientoTarjeta} onChange={handleNuevoMedioChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-center text-slate-700 outline-none bg-white h-[40px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required={isAddingNewMedio} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Responsable de Gestión */}
                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Responsable *</label>
                            <select name="quienGestiono" value={formData.quienGestiono} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required>
                                <option value="">Seleccione...</option>
                                {sortedColaboradores.map(p => (
                                    <option key={p.id} value={p.nombre}>{p.nombre}</option>
                                ))}
                            </select>
                        </div>

                        {/* N° de Trámite */}
                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Trámite</label>
                            <input type="text" name="tramite" value={formData.tramite} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="N° de trámite oficial..." />
                        </div>

                        {/* Estado */}
                        <div className="md:col-span-2">
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado de Gestión *</label>
                            <select name="estadoGestion" value={formData.estadoGestion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-blue-700 outline-none bg-red-50/20 h-[46px] focus:border-blue-500 focus:ring-1 focus:ring-red-100 transition-all" required>
                                <option value="">Seleccione...</option>
                                {estadosGestion.map(e => (
                                    <option key={e} value={e}>{e}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Observaciones del Endoso */}
                    <div className="mt-6 text-left">
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Observaciones</label>
                        <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 outline-none bg-white min-h-[120px] resize-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all placeholder:text-slate-300" placeholder="Escriba los motivos de la solicitud de endoso o detalles adicionales de la gestión..." />
                    </div>
                </fieldset>

                {/* BOTONERA DE ACCIONES INTEGRADA */}
                <div className="pt-6 border-t border-slate-100 flex justify-end gap-3 bg-white relative z-10">
                    <button 
                        type="button" 
                        onClick={onCancel} 
                        className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs shadow-sm hover:bg-slate-50 transition-all transform active:scale-95 select-none"
                    >
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        disabled={isSubmitting} 
                        className="px-8 py-2.5 rounded-full bg-blue-600 text-white font-bold shadow-lg shadow-blue-100 disabled:opacity-50 uppercase text-xs hover:bg-blue-700 transition-all transform active:scale-95 select-none min-w-[160px]"
                    >
                        {isSubmitting ? 'Guardando...' : 'Guardar Endoso'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FormularioEndoso;